import type { LucideIcon } from 'lucide-react'
import { Activity, Cloud, Gauge, ListChecks, MessageCircleQuestion, NotebookPen, Scale, Star, TrendingUp, Zap } from 'lucide-react'
import type { LiveActivityRole, LiveActivityType } from '@/api/live'

/**
 * The activities an admin can add to a course: the "shop window" in the Add dialog. Adding a new kind of activity
 * to the product starts here: one new entry with its description, when to use it, and an example.
 */
export type CatalogueCategory = 'learning' | 'engage' | 'feedback'

export const CATEGORY_LABEL: Record<CatalogueCategory, string> = {
  learning: 'Measure learning',
  engage: 'Engage the room',
  feedback: 'Feedback',
}

export interface CatalogueEntry {
  id: string
  type: LiveActivityType
  /** The role it takes on the course, if any. A role can be used once per course. */
  role: LiveActivityRole | null
  /** The name it is given when added (the admin can change it). */
  name: string
  icon: LucideIcon
  category: CatalogueCategory
  /** One line for the card. */
  tagline: string
  /** What it does. */
  what: string
  /** When a trainer might use it. */
  when: string
  /** A worked example, one line each. */
  example: string[]
  /** Short labels shown on the card. */
  tags: string[]
  /** Anything worth knowing before adding it. */
  note?: string
  /** A screenshot to show in the details, once there is one (a path under /public or a full URL). */
  preview?: { src: string; alt: string }
  /** A quiz that opens with one blank question of this type, rather than a choice question. */
  starterQuestion?: 'truefalse' | 'choice'
}

export const CATALOGUE: CatalogueEntry[] = [
  {
    id: 'pre-quiz', type: 'quiz', role: 'pre', name: 'Pre-course quiz', icon: ListChecks, category: 'learning',
    tagline: 'See what people already know before you start.',
    what: 'A short quiz attendees answer on their phones at the start. Choose single choice (one right answer) or multiple choice (tick all that apply) for each question, mark the right answers, and CertSlate scores it for you. Everyone answers at their own pace, anonymously.',
    when: 'At the start of a course, to find out where the group is and where to focus. Pair it with a post-course quiz to show how much people learned.',
    example: [
      'How many chest compressions should you give per minute?',
      '60  ·  100–120  ·  200   (100–120 marked as correct)',
    ],
    tags: ['Scored', 'Pairs with post-course quiz'],
  },
  {
    id: 'post-quiz', type: 'quiz', role: 'post', name: 'Post-course quiz', icon: ListChecks, category: 'learning',
    tagline: 'Show how much people learned.',
    what: 'The same idea at the end of the course. Re-use the pre-course questions and CertSlate compares the two like for like: the group’s learning gain, counting only people who did both. Stop it and show the answers, and each attendee sees their own score on their phone, with which questions they got right or wrong.',
    when: 'At the end of the session, once the teaching is done. It works best with the same questions as the pre-course quiz.',
    example: [
      'Ask the compression-rate question again.',
      'The group moves from 40% correct to 90%: a learning gain of +50 points, shown on the booking.',
    ],
    tags: ['Scored', 'Learning gain'],
    note: 'If the course already has a pre-course quiz, its questions are copied across for you.',
  },
  {
    id: 'knowledge-check', type: 'quiz', role: null, name: 'Knowledge check', icon: NotebookPen, category: 'learning',
    tagline: 'A quiz you can drop in at any point.',
    what: 'Questions on any topic, with optional right answers, that you run whenever you choose. It isn’t paired with anything, so it doesn’t affect the learning-gain figures.',
    when: 'After a module, to check understanding before moving on, or as a lively recap at the end of the day.',
    example: ['After the CPR practical: “What is the first thing you do when someone is unresponsive?”'],
    tags: ['Scored', 'Add as many as you like'],
  },
  {
    id: 'myth-or-fact', type: 'quiz', role: null, name: 'Myth or fact', icon: Scale, category: 'learning', starterQuestion: 'truefalse',
    tagline: 'True or false, with the reason shown.',
    what: 'A run of true/false statements. Mark whether each is true, and add a short explanation that appears when you reveal the answers, so the quiz teaches as well as tests.',
    when: 'To bust common misconceptions, as a quick energiser after a break, or to recap a module.',
    example: ['“You should run cold water over a burn for 20 minutes.”  True', 'Explanation: cooling limits the damage and eases the pain.'],
    tags: ['Scored', 'With explanations'],
    note: 'Any question in any quiz can be set to true/false and given an explanation.',
  },
  {
    id: 'confidence-before', type: 'confidence', role: 'before', name: 'Confidence before', icon: Gauge, category: 'learning',
    tagline: 'How confident do people feel before the course?',
    what: 'Attendees rate how confident they feel about each skill, from 1 (not at all) to 10 (very). For practical skills this is often more telling than a test: people can know the theory and still not feel able to act.',
    when: 'At the start of the course. Run a matching “after” check at the end to show the change.',
    example: ['Using an AED: 3 / 10', 'Putting someone in the recovery position: 4 / 10'],
    tags: ['Pairs with “after”'],
  },
  {
    id: 'confidence-after', type: 'confidence', role: 'after', name: 'Confidence after', icon: TrendingUp, category: 'learning',
    tagline: 'Show how much more confident people feel.',
    what: 'The same statements again at the end. CertSlate shows the average change, counting only people who answered both, which is a powerful result to share with the client.',
    when: 'At the end of the course, after the practical work.',
    example: ['Using an AED: 3 / 10 before, 8 / 10 after: a confidence gain of +5.'],
    tags: ['Confidence gain'],
    note: 'If the course already has a “before” check, its statements are copied across so the two match.',
  },
  {
    id: 'word-cloud', type: 'wordcloud', role: null, name: 'Word cloud', icon: Cloud, category: 'engage',
    tagline: 'Get the whole room contributing, fast.',
    what: 'Attendees send between one and five words and the projector shows them growing the more often they come up. You can hide any word you don’t want on screen.',
    when: 'As an icebreaker, to open a discussion, or to capture the mood of the room at the end.',
    example: ['“One word for how you feel about giving first aid.”', 'calm · ready · nervous · nervous · keen · ready · nervous…'],
    tags: ['No right answers', 'Great on the projector'],
  },
  {
    id: 'pulse', type: 'pulse', role: null, name: 'Understanding check', icon: Activity, category: 'engage',
    tagline: 'Check how well it is landing, in one tap.',
    what: 'Attendees tap with you, not sure or lost, and the split shows live on your screen and the projector. Run it again whenever you like: each run is kept as a round, so you can see understanding move through the session.',
    when: 'After a tricky topic, before moving on, or every half hour to keep a finger on the pulse. If a lot of people are lost, you know to slow down.',
    example: ['“How are you following so far?”', 'Round 1: 6 with you · 3 not sure · 2 lost.  Round 2, after a recap: 10 with you · 1 not sure · 0 lost.'],
    tags: ['One tap', 'Run as many times as you like'],
  },
  {
    id: 'fastest-finger', type: 'race', role: null, name: 'Fastest finger', icon: Zap, category: 'engage', starterQuestion: 'choice',
    tagline: 'A quick-fire race: who taps the right answer first?',
    what: 'A game of single-answer questions, played one at a time. Each starts with a 3-2-1 countdown, then everyone races to tap the right answer on their phone. When everyone has answered (or you choose, or a time limit runs out) the answer appears with a leaderboard of the quickest correct answers, with names and times. Everyone who is right scores a point, the three quickest score a bonus, and the scores add up to a winner at the end.',
    when: 'To lift the room after lunch, to recap a module as a game, or to finish the day on a high.',
    example: ['“How many chest compressions per minute?”   100–120 ✓', '1st Priya 0.84s  ·  2nd Tom 1.12s  ·  3rd Dee 1.47s'],
    tags: ['Game', 'Names on the projector', 'Not saved to the booking'],
    note: 'Just for fun: the scores stay in the room and aren’t kept with the booking or counted in analytics. Names show on the projector, so anyone who joins without one is invited to add a first name.',
  },
  {
    id: 'live-qa', type: 'qa', role: null, name: 'Live Q&A', icon: MessageCircleQuestion, category: 'engage',
    tagline: 'Let people ask and upvote questions.',
    what: 'Attendees ask questions anonymously and upvote each other’s, so the ones most people want answered rise to the top. It runs on its own as the only open activity, so there is nothing to confuse it with, and you can mark questions answered or hide them.',
    when: 'Open it as a dedicated slot, for example after a module or at the end, when you want to take the room’s questions.',
    example: ['“Does the certificate cover offshore work?”   ▲ 7'],
    tags: ['Runs on its own'],
  },
  {
    id: 'feedback', type: 'quiz', role: 'feedback', name: 'Feedback form', icon: Star, category: 'feedback',
    tagline: 'Collect anonymous ratings and comments.',
    what: 'A short form with a rating from 1 to 10 and optional comments. The first rating becomes the headline feedback score on the booking, in analytics and on the trainer’s page.',
    when: 'The last thing in the session, while everyone is still in the room. You’ll get far more responses than by email afterwards.',
    example: ['Overall, how would you rate this course? (1–10)', 'What worked well?', 'What could be improved?'],
    tags: ['Feeds analytics'],
    note: 'Even if you don’t add one, attendees get a built-in feedback form at the end. Add your own to change the questions.',
  },
]

/** The three that most courses want: a before, an after, and feedback. */
export const STARTER_SET = ['pre-quiz', 'post-quiz', 'feedback']

export const entryById = (id: string) => CATALOGUE.find(e => e.id === id)!
